import {
  cannedWorkPackageGroupPages,
  cannedWorkPackagesInfiniteQuery,
  useArchiveCannedWorkPackageMutation,
  useRestoreCannedWorkPackageMutation,
} from "@common/api/quoting/canned"
import type {
  CannedWorkPackageDetailPublic,
  CannedWorkPackageStatus,
} from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { Checkbox } from "@common/components/ui/checkbox"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useRangeToggle } from "@common/hooks/use-range-toggle"
import { formatShortDate } from "@common/lib/date-utils"
import { cn } from "@common/lib/utils"
import { handleError } from "@common/utils"
import {
  type QueryClient,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { ClipboardList } from "lucide-react"
import { useState } from "react"
import { GroupedTable } from "@/components/layout/grouped-table"
import { ListEmpty } from "@/components/layout/list-empty"
import {
  type ListColumns,
  type ListSort,
  ListTableCell,
  ListTableCheckbox,
  ListTableHeader,
  ListTableReveal,
  ListTableRow,
  ListTableSort,
  ListTableStamp,
  ListTableText,
} from "@/components/layout/list-table"
import {
  ArchiveMenuItem,
  DeleteMenuItem,
  ListTableMenu,
} from "@/components/layout/list-table-menu"
import { LoadMoreSentinel } from "@/components/layout/load-more-sentinel"
import { useGroupPages } from "@/components/layout/use-group-paging"
import { type ColumnArrange, useColumnLayout } from "@/hooks/use-column-layout"
import useCustomToast from "@/hooks/use-custom-toast"
import type { GroupLabel } from "@/lib/grouping"
import { quoteApplicabilityLabel, quotePrice, quoteStatus } from "../-lib/price"
import {
  hasQuoteFilters,
  isQuoteSortable,
  QUOTE_COLUMN_LABELS,
  QUOTE_COLUMN_SIZING,
  type QuoteColumn,
  type QuoteFilters,
  type QuoteGrouping,
  type QuoteSlot,
  type QuoteSort,
  quoteDisplay,
  quoteGroupParams,
  quoteListParams,
  quoteSlotsOf,
  quoteSort,
  quoteStatusLabel,
} from "../-lib/quote-list"
import { QuoteSelectionBar } from "./quote-selection-bar"
import { QuoteStatusIcon } from "./review-status-icon"

const SELECT_ALL_REVEAL =
  "opacity-0 group-hover/header:opacity-100 focus:opacity-100"

const ROW_LINK = cn(
  "col-span-full grid grid-cols-subgrid",
  "text-foreground hover:no-underline",
)

function columnsOf(slots: QuoteSlot[]): ListColumns<QuoteSlot> {
  return {
    slots,
    end: ["tasks", "price"],
    muted: ["number", "applies_to", "created"],
  }
}

type Picked = ReadonlySet<string>

type Selection = {
  slots: QuoteSlot[]
  style: React.CSSProperties
  arrange: ColumnArrange<QuoteSlot>
  sort: ListSort<QuoteSort>
  picked: Picked
  onPick: (picked: Picked) => void
  onSort: (column: QuoteSort) => void
}

type Table = {
  style: React.CSSProperties
  header: React.ReactNode
  renderRows: (
    rows: CannedWorkPackageDetailPublic[],
    order: CannedWorkPackageDetailPublic[],
  ) => React.ReactNode
}

function statusLabel(status: CannedWorkPackageStatus): GroupLabel {
  return {
    key: status,
    label: quoteStatusLabel(status),
    icon: <QuoteStatusIcon status={status} />,
  }
}

export function prefetchQuotes(
  queryClient: QueryClient,
  filters: QuoteFilters,
): Promise<unknown> {
  const { group } = quoteDisplay(filters)
  return group
    ? queryClient.ensureQueryData(
        cannedWorkPackageGroupPages.groups(quoteGroupParams(filters, group)),
      )
    : queryClient.ensureInfiniteQueryData(
        cannedWorkPackagesInfiniteQuery(quoteListParams(filters)),
      )
}

export function QuoteTable({
  filters,
  columns,
  onSort,
  onClear,
}: {
  filters: QuoteFilters
  columns: QuoteColumn[]
  onSort: (column: QuoteSort) => void
  onClear: () => void
}): React.ReactElement {
  const canDelete = useHasPermission("canned_work_package:delete")
  const [picked, setPicked] = useState<Picked>(new Set())
  const layout = useColumnLayout(
    quoteSlotsOf(columns, canDelete),
    QUOTE_COLUMN_SIZING,
  )
  const selection: Selection = {
    slots: layout.slots,
    style: { gridTemplateColumns: layout.template },
    arrange: layout.arrange,
    sort: quoteSort(filters),
    picked,
    onPick: setPicked,
    onSort,
  }
  const filtered = Boolean(filters.search) || hasQuoteFilters(filters)
  const empty = <NoQuotes onClear={filtered ? onClear : undefined} />
  const { group } = quoteDisplay(filters)

  return group ? (
    <GroupedQuoteTable
      filters={filters}
      group={group}
      selection={selection}
      empty={empty}
    />
  ) : (
    <QuoteList filters={filters} selection={selection} empty={empty} />
  )
}

function GroupedQuoteTable({
  filters,
  group,
  selection,
  empty,
}: {
  filters: QuoteFilters
  group: QuoteGrouping
  selection: Selection
  empty: React.ReactElement
}): React.ReactElement {
  const { data, paging } = useGroupPages(
    cannedWorkPackageGroupPages,
    quoteGroupParams(filters, group),
    (item) => item.id,
  )
  const groupOf = (
    item: CannedWorkPackageDetailPublic,
    grouping: QuoteGrouping,
  ): GroupLabel => {
    switch (grouping) {
      case "status":
        return statusLabel(quoteStatus(item.tasks))
    }
  }

  if (data.groups.length === 0) return empty

  return (
    <SelectableQuotes quotes={paging.rows} selection={selection}>
      {(table) => (
        <GroupedTable
          paging={paging}
          style={table.style}
          header={table.header}
          group={group}
          subgroup={undefined}
          groupOf={groupOf}
          renderRows={table.renderRows}
          sticky
        />
      )}
    </SelectableQuotes>
  )
}

function QuoteList({
  filters,
  selection,
  empty,
}: {
  filters: QuoteFilters
  selection: Selection
  empty: React.ReactElement
}): React.ReactElement {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useSuspenseInfiniteQuery(
    cannedWorkPackagesInfiniteQuery(quoteListParams(filters)),
  )
  const quotes = data.pages.flatMap((page) => page.data)

  if (quotes.length === 0) return empty

  return (
    <SelectableQuotes quotes={quotes} selection={selection}>
      {(table) => (
        <>
          <GridTable style={table.style}>
            {table.header}
            {table.renderRows(quotes, quotes)}
          </GridTable>
          <LoadMoreSentinel
            noun="template quotes"
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isError={isFetchNextPageError}
            fetchNextPage={fetchNextPage}
          />
        </>
      )}
    </SelectableQuotes>
  )
}

function SelectableQuotes({
  quotes,
  selection: { slots, style, arrange, sort, picked, onPick, onSort },
  children,
}: {
  quotes: CannedWorkPackageDetailPublic[]
  selection: Selection
  children: (table: Table) => React.ReactNode
}): React.ReactElement {
  const rangeToggle = useRangeToggle<string>()
  const chosen = quotes.filter((item) => picked.has(item.id))
  const pick = (rows: CannedWorkPackageDetailPublic[]) =>
    onPick(new Set(rows.map((item) => item.id)))
  const toggle = (
    order: CannedWorkPackageDetailPublic[],
    id: string,
    range: boolean,
  ) =>
    onPick(
      rangeToggle(
        order.map((item) => item.id),
        picked,
        id,
        range,
      ),
    )

  return (
    <>
      {children({
        style,
        header: (
          <QuoteHeader
            slots={slots}
            sort={sort}
            arrange={arrange}
            all={quotes.length > 0 && chosen.length === quotes.length}
            some={chosen.length > 0}
            onSort={onSort}
            onAll={(on) => pick(on ? quotes : [])}
          />
        ),
        renderRows: (rows, order) =>
          rows.map((item) => (
            <QuoteRow
              key={item.id}
              item={item}
              slots={slots}
              checked={picked.has(item.id)}
              onCheck={(range) => toggle(order, item.id, range)}
            />
          )),
      })}
      {chosen.length > 0 && (
        <QuoteSelectionBar quotes={chosen} onClear={() => pick([])} />
      )}
    </>
  )
}

function NoQuotes({
  onClear,
}: {
  onClear: (() => void) | undefined
}): React.ReactElement {
  return (
    <ListEmpty
      icon={ClipboardList}
      title={onClear ? "No matching template quotes" : "No template quotes"}
      body={
        onClear
          ? "Try a different search or filter."
          : "Create a template quote to start quotes from a set of template tasks."
      }
      onClear={onClear}
    />
  )
}

function QuoteHeader({
  slots,
  sort,
  arrange,
  all,
  some,
  onSort,
  onAll,
}: {
  slots: QuoteSlot[]
  sort: ListSort<QuoteSort>
  arrange: ColumnArrange<QuoteSlot>
  all: boolean
  some: boolean
  onSort: (column: QuoteSort) => void
  onAll: (on: boolean) => void
}): React.ReactElement {
  return (
    <ListTableHeader columns={columnsOf(slots)} sort={sort} arrange={arrange}>
      {(slot) => {
        switch (slot) {
          case "select":
            return (
              <Checkbox
                aria-label="Select all template quotes"
                checked={all}
                className={cn(!some && SELECT_ALL_REVEAL)}
                onCheckedChange={(next) => onAll(next === true)}
              />
            )
          case "status":
          case "actions":
            return null
          default:
            return isQuoteSortable(slot) ? (
              <ListTableSort
                column={slot}
                label={QUOTE_COLUMN_LABELS[slot]}
                sort={sort}
                onSort={onSort}
              />
            ) : (
              <ListTableText>{QUOTE_COLUMN_LABELS[slot]}</ListTableText>
            )
        }
      }}
    </ListTableHeader>
  )
}

function QuoteRow({
  item,
  slots,
  checked,
  onCheck,
}: {
  item: CannedWorkPackageDetailPublic
  slots: QuoteSlot[]
  checked: boolean
  onCheck: (range: boolean) => void
}): React.ReactElement {
  const columns = columnsOf(slots)

  return (
    <ListTableRow selected={checked} dimmed={Boolean(item.archived_at)}>
      <Link
        to="/templates/quotes/$packageId"
        params={{ packageId: item.id }}
        className={ROW_LINK}
      >
        {slots.map((slot) => (
          <ListTableCell
            key={slot}
            columns={columns}
            slot={slot}
            onClick={
              slot === "select" || slot === "actions" ? stayOnList : undefined
            }
          >
            {slot === "select" ? (
              <ListTableCheckbox
                label={`Select ${item.name}`}
                checked={checked}
                onToggle={onCheck}
              />
            ) : slot === "actions" ? (
              <QuoteActions item={item} />
            ) : (
              <SlotValue slot={slot} item={item} />
            )}
          </ListTableCell>
        ))}
      </Link>
    </ListTableRow>
  )
}

function stayOnList(event: React.MouseEvent): void {
  event.preventDefault()
  event.stopPropagation()
}

function QuoteActions({
  item,
}: {
  item: CannedWorkPackageDetailPublic
}): React.ReactElement {
  const navigate = useNavigate()
  const archive = useArchiveCannedWorkPackageMutation()
  const restore = useRestoreCannedWorkPackageMutation()
  const showToast = useCustomToast()
  const archived = item.archived_at !== null

  return (
    <ListTableReveal>
      <ListTableMenu label={item.name}>
        <ArchiveMenuItem
          archived={archived}
          disabled={archive.isPending || restore.isPending}
          onSelect={() =>
            (archived ? restore : archive).mutate(
              { package_id: item.id },
              {
                onSuccess: () =>
                  showToast(
                    archived ? "Restored" : "Archived",
                    `${item.name} ${archived ? "restored" : "archived"}.`,
                    "success",
                  ),
                onError: (error) => handleError(error, showToast),
              },
            )
          }
        />
        <DeleteMenuItem
          onSelect={() =>
            navigate({
              from: "/templates/quotes",
              to: "/templates/quotes/delete",
              search: (previous) => ({ ...previous, ids: [item.id] }),
            })
          }
        />
      </ListTableMenu>
    </ListTableReveal>
  )
}

function SlotValue({
  slot,
  item,
}: {
  slot: QuoteSlot
  item: CannedWorkPackageDetailPublic
}): React.ReactNode {
  switch (slot) {
    case "select":
    case "actions":
      return null
    case "status":
      return <QuoteStatusIcon status={quoteStatus(item.tasks)} />
    case "number":
      return <ListTableText>{item.number}</ListTableText>
    case "name":
      return <ListTableText>{item.name}</ListTableText>
    case "applies_to":
      return (
        <ListTableText>
          {quoteApplicabilityLabel(item.type_certificate_model_variants)}
        </ListTableText>
      )
    case "tasks":
      return (
        <ListTableText>
          {item.tasks.filter((task) => !task.deleted_at).length}
        </ListTableText>
      )
    case "price":
      return <ListTableText>{quotePrice(item.tasks)}</ListTableText>
    case "created":
      return (
        <ListTableStamp
          avatar={item.created_by && <UserInitials user={item.created_by} />}
        >
          {formatShortDate(item.created_at)}
        </ListTableStamp>
      )
  }
}
